import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Check, X } from "lucide-react";
import { eur } from "./format";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Iter 169 — Oferta por incidencia de Checklist dentro del enlace del presupuesto
 *  (decisión independiente: no se mezcla con aceptar/rechazar el presupuesto). */
export function ChecklistOfferSection({ token, offers, onChanged }) {
  const { t } = useT();
  if (!offers?.length) return null;
  return offers.map((o) => <OfferCard key={o.id} token={token} offer={o} t={t} onChanged={onChanged} />);
}

function OfferCard({ token, offer, t, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const decided = offer.status === "accepted" || offer.status === "rejected";

  const decide = async (kind) => {
    if (inFlight.current || busy) return; // anti doble clic
    inFlight.current = true; setBusy(true);
    try {
      await axios.post(`${API}/public/quote/${token}/offer/${offer.id}/${kind}`);
      toast.success(kind === "accept" ? t("offer_accepted") : t("offer_rejected"));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t("err_already") : t("err_generic"));
      onChanged?.();
    } finally { setBusy(false); inFlight.current = false; }
  };

  return (
    <div className="ds-card p-5 border-2 border-[#6E8B3D]" data-testid={`checklist-offer-${offer.id}`}>
      <div className="flex items-center gap-2 font-display font-bold">
        <Sparkles size={16} className="text-[#6E8B3D]" /> {t("offer_title")}
      </div>
      <p className="text-sm mt-2 text-[#525252]">{t("offer_intro")}</p>
      <ul className="mt-1 space-y-1" data-testid="offer-incidents">
        {offer.incidents.map((i) => (
          <li key={i.key} className="text-sm font-semibold text-[#9B2C1F] bg-[#FDEDE7] px-3 py-1.5 border-l-2 border-[#B4381F]">{t(`cl_${i.key}`) === `cl_${i.key}` ? i.label : t(`cl_${i.key}`)}</li>
        ))}
      </ul>
      <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">{t("offer_special", { n: offer.discount_pct })}</div>
      <div className="mt-1 divide-y divide-[#EEEEEA]">
        {offer.lines.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-3 py-2" data-testid={`offer-public-line-${l.id}`}>
            <div>
              <div className="font-semibold text-sm">{l.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}</div>
              {l.warranty_months > 0 && <div className="text-[11px] text-[#525252]">{t("warranty_months", { n: l.warranty_months })}</div>}
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="text-xs text-[#8A8A82] line-through">{eur(l.regular_total)}</div>
              <div className="font-display font-black text-lg">{eur(l.offer_price)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between border-t-2 border-[#111110] pt-3 mt-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{t("offer_total")}</span>
        <span className="font-display font-black text-2xl" data-testid="offer-public-total">{eur(offer.total)}</span>
      </div>
      {decided ? (
        <div className={`mt-4 px-3 py-2 text-sm font-semibold ${offer.status === "accepted" ? "bg-[#F0F4E8] text-[#3F5222]" : "bg-[#FDEDE7] text-[#9B2C1F]"}`} data-testid="offer-public-state">
          {offer.status === "accepted" ? t("offer_accepted") : t("offer_rejected")}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button type="button" onClick={() => decide("accept")} disabled={busy} data-testid="offer-public-accept"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-widest bg-[#6E8B3D] text-white font-bold disabled:opacity-50">
            <Check size={14} /> {t("offer_accept")}
          </button>
          <button type="button" onClick={() => decide("reject")} disabled={busy} data-testid="offer-public-reject"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-widest border border-[#111110] bg-white disabled:opacity-50">
            <X size={14} /> {t("offer_reject")}
          </button>
        </div>
      )}
    </div>
  );
}
